import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { MembershipJoin } from "../components/MembershipJoin";
import { CHERRY_URL, IMG } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tier = ({ m, featured, onJoin }) => (
  <div className={`flex flex-col p-10 md:p-12 ${featured ? "bg-neutral-950 text-white" : "border border-neutral-200 bg-white"}`} data-testid={`membership-tier-${m.id}`}>
    <p className={`text-[10px] uppercase tracking-[0.3em] ${featured ? "text-neutral-400" : "text-neutral-500"}`}>Membership</p>
    <h3 className="font-display mt-3 text-4xl font-light">{m.name}</h3>
    <p className={`font-display mt-6 ${m.price.startsWith("$") ? "text-3xl" : "text-2xl"}`} data-testid="membership-price">{m.price}</p>
    {m.period && <p className={`text-xs uppercase tracking-[0.2em] ${featured ? "text-neutral-400" : "text-neutral-500"}`}>{m.period}</p>}
    {m.description && <p className={`mt-6 text-sm font-light leading-relaxed ${featured ? "text-neutral-300" : "text-neutral-600"}`}>{m.description}</p>}
    {m.perks?.length > 0 && (
      <ul className="mt-8 space-y-3 text-sm font-light">
        {m.perks.map((p) => <li key={p} className="flex gap-3"><Check size={15} strokeWidth={1.25} className="mt-0.5 shrink-0" />{p}</li>)}
      </ul>
    )}
    <div className="mt-auto pt-10">
      {m.is_sample || !m.price.startsWith("$") || /vip/i.test(m.name) ? (
        <Link to="/contact" className={featured ? "btn-ghost-light w-full" : "btn-line w-full"} data-testid={`membership-ask-${m.id}`}>Contact Us to Join</Link>
      ) : (
        <button type="button" onClick={() => onJoin(m)} className={featured ? "block w-full bg-white px-8 py-4 text-center text-[11px] font-medium uppercase tracking-[0.2em] text-neutral-950 transition-colors hover:bg-neutral-200" : "btn-ink w-full"} data-testid={`membership-join-${m.id}`}>Join Now</button>
      )}
    </div>
  </div>
);

export default function Memberships() {
  const [tiers, setTiers] = useState([]);
  const [joining, setJoining] = useState(null);
  useEffect(() => {
    axios.get(`${API}/memberships`).then(({ data }) => setTiers(data)).catch(() => setTiers([]));
  }, []);
  return (
    <main data-testid="memberships-page">
      <section className="grid lg:grid-cols-2">
        <div className="flex flex-col justify-center px-6 py-20 md:px-16 lg:py-32">
          <p className="kicker rise">Memberships & Packages</p>
          <h1 className="font-display rise d1 mt-4 text-5xl font-light leading-[1.05] text-neutral-950 sm:text-6xl">Radiance,<br /><em>maintained.</em></h1>
          <p className="rise d2 mt-6 max-w-md text-base font-light leading-relaxed text-neutral-600">For clients who invest in long-term results. A membership keeps your personalized plan on schedule, with priority attention from our team.</p>
          <div className="rise d3 mt-10 flex flex-wrap gap-4">
            <a href="#tiers" className="btn-ink" data-testid="memberships-view-tiers">View Memberships</a>
            <Link to="/contact" className="btn-line" data-testid="memberships-consult-btn">Book a Consultation</Link>
          </div>
        </div>
        <div className="h-[420px] overflow-hidden lg:h-[680px]"><img src={IMG.museHands} alt="Lux Veritae muse" className="h-full w-full object-cover" /></div>
      </section>
      <section id="tiers" className="scroll-mt-[200px] bg-[hsl(var(--stone))] py-24 md:py-32">
        <div className="mx-auto max-w-[1300px] px-4 md:px-8">
          <Reveal className="mb-14 text-center">
            <p className="kicker">Choose Your Rhythm</p>
            <h2 className="font-display mt-3 text-4xl font-light text-neutral-950 md:text-5xl">Membership Tiers</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4" data-testid="membership-tiers">
            {tiers.map((m, i) => <Tier key={m.id} m={m} featured={i === 1} onJoin={setJoining} />)}
          </div>
          <MembershipJoin tier={joining} onClose={() => setJoining(null)} />
          <p className="mt-12 text-center text-sm font-light text-neutral-600">
            Prefer to spread the investment? <a href={CHERRY_URL} target="_blank" rel="noreferrer" className="underline" data-testid="memberships-cherry-link">Finance with Cherry</a>.
          </p>
        </div>
      </section>
    </main>
  );
}
